import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  Paper,
  Typography,
  Button,
} from '@mui/material';

const Step3PengalamanKerja = ({ form, setForm }) => {
  // Fungsi untuk memformat angka dengan pemisah ribuan menggunakan titik
  const formatNumberWithDots = (value) => {
    if (value === null || value === undefined) return '';

    // Hapus semua karakter non-digit
    const digits = value.toString().replace(/\D/g, '');
    if (!digits) return '';

    // Tambahkan pemisah ribuan dengan titik
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };
  
  // Fungsi untuk mengembalikan angka ke bentuk asli (tanpa titik)
  const parseNumberFromDots = useCallback((formattedValue) => {
    if (!formattedValue) return '';
    return formattedValue.toString().replace(/\./g, '');
  }, []);
  
  // Fungsi untuk memformat allowance (bisa angka atau text)
  const formatAllowance = (value) => {
    if (value === null || value === undefined) return '';

    const stringValue = value.toString();

    // Jika hanya berisi digit dan pemisah yang salah (mis. 1.0.0.0.0.000), bersihkan dulu ke digit murni
    const onlyDigits = stringValue.replace(/\D/g, '');
    if (/^\d+$/.test(onlyDigits)) {
      return formatNumberWithDots(onlyDigits);
    }

    // Jika ada campuran teks dan angka, format setiap angka ke ribuan bertitik
    const parts = stringValue.split(/(\d[\d.,]*)/g); // pecah berdasarkan segmen angka
    const formatted = parts
      .map(seg => {
        if (/^\d[\d.,]*$/.test(seg)) {
          const digits = seg.replace(/\D/g, '');
          return digits ? formatNumberWithDots(digits) : seg;
        }
        return seg;
      })
      .join('');

    return formatted;
  };
  
  // Fungsi untuk membersihkan allowance sebelum dikirim ke backend
  const parseAllowance = useCallback((formattedValue) => {
    if (formattedValue === null || formattedValue === undefined) return '';

    const stringValue = formattedValue.toString();

    // Jika hanya angka bertitik, hapus pemisah dan kembalikan angka murni
    if (/^\d+(?:[\d.,]\d+)*$/.test(stringValue)) {
      return parseNumberFromDots(stringValue);
    }

    // Jika campuran teks-angka, kembalikan angka-angka ke bentuk murni (tanpa titik), sisakan teks apa adanya
    const parts = stringValue.split(/(\d[\d.,]*)/g);
    const parsed = parts
      .map(seg => (/^\d[\d.,]*$/.test(seg) ? parseNumberFromDots(seg) : seg))
      .join('');

    return parsed;
  }, [parseNumberFromDots]);
  // Debug logs dapat diaktifkan saat perlu
  // console.log('Step3PengalamanKerja rendering with workHistory:', form.workHistory);
  // useEffect(() => {
  //   console.log('Step3PengalamanKerja mounted with workHistory:', form.workHistory);
  //   console.log('Form state:', form);
  // }, []);
  
  const [internalWorkHistory, setInternalWorkHistory] = useState(() => {
    const workHistoryData = Array.isArray(form.workHistory) ? form.workHistory : [{ 
      companyName: '', 
      period: '', 
      position: '', 
      basicSalary: '', 
      allowance: '', 
      movingReason: '',
      jobDescription: ''
    }];
    
    // Format gaji dan allowance dengan pemisah ribuan ketika data dimuat
    return workHistoryData.map(item => ({
      ...item,
      basicSalary: formatNumberWithDots(item.basicSalary),
      allowance: formatAllowance(item.allowance)
    }));
  });

  // Sync internal state with parent form state
  useEffect(() => {
    // Bersihkan format gaji sebelum dikirim ke parent form
    const cleanedWorkHistory = internalWorkHistory.map(item => ({
      ...item,
      basicSalary: parseNumberFromDots(item.basicSalary),
      allowance: parseAllowance(item.allowance),
      jobDescription: item.jobDescription || ''
    }));
    
    setForm(prev => ({
      ...prev,
      workHistory: cleanedWorkHistory
    }));
  }, [internalWorkHistory, setForm, parseAllowance, parseNumberFromDots]);

  // Helper: update array item by index/field
  const handleArrayChange = (index, field, value) => {
    setInternalWorkHistory(prev => {
      const current = Array.isArray(prev) ? prev : [];
      const newArray = [...current];
      
      // Format gaji pokok dan allowance dengan pemisah ribuan
      let processedValue = value;
      if (field === 'basicSalary') {
        processedValue = formatNumberWithDots(value);
      } else if (field === 'allowance') {
        processedValue = formatAllowance(value);
      }
      
      newArray[index] = { ...newArray[index], [field]: processedValue };
      return newArray;
    });
  };

  // Helper: remove array item by index
  const handleArrayRemove = (index) => {
    setInternalWorkHistory(prev => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.length <= 1) return current; // Prevent removing last item
      const newArray = [...current];
      newArray.splice(index, 1);
      return newArray;
    });
  };

  // Helper: add new array item
  const addWorkHistory = () => {
    setInternalWorkHistory(prev => {
      const current = Array.isArray(prev) ? prev : [];
      const newItem = { 
        companyName: '', 
        period: '', 
        position: '', 
        basicSalary: '', 
        allowance: '', 
        movingReason: '',
        jobDescription: ''
      };
      
      // Format gaji untuk item baru (meskipun kosong, untuk konsistensi)
      return [...current, {
        ...newItem,
        basicSalary: formatNumberWithDots(newItem.basicSalary),
        allowance: formatNumberWithDots(newItem.allowance)
      }];
    });
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', my: 4, p: 2 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: '#ffffff', 
          mb: 3,
          border: '2px solid #1976d2',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)'
        }}
      >
        <Grid container spacing={3}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                pb: 2,
                borderBottom: '2px solid #e3f2fd'
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#1976d2',
                  fontSize: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <span>💼</span>
                Pengalaman Kerja
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666', 
                mb: 3,
                fontStyle: 'italic',
                fontSize: '0.95rem'
              }}
            >
              Silakan isi data pengalaman kerja dan informasi terkait untuk melengkapi profil Anda.
            </Typography>
          </Grid>

          {/* Work History Table */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#495057' }}>
                Riwayat Pekerjaan
              </Typography>
              
              {/* Table Header */}
              <Grid container spacing={1} sx={{ mb: 2, fontWeight: 'bold', backgroundColor: '#e3f2fd', p: 1, borderRadius: 1 }}>
                <Grid item xs={0.5}>No</Grid>
                <Grid item xs={2}>Nama Perusahaan<br/>(Company Name)</Grid>
                <Grid item xs={1.5}>Tahun<br/>(Period)</Grid>
                <Grid item xs={2}>Jabatan<br/>(Position)</Grid>
                <Grid item xs={1.5}>Gaji Pokok<br/>(Basic Salary)</Grid>
                <Grid item xs={2}>Tunjangan Lain<br/>(Allowance)</Grid>
                <Grid item xs={2}>Alasan Pindah<br/>(Moving Reason)</Grid>
                <Grid item xs={0.5}>Aksi</Grid>
              </Grid>
              
              {/* Table Rows */}
              {internalWorkHistory.map((row, idx) => (
                <Grid container spacing={1} key={`work-history-${idx}`} sx={{ mb: 1, alignItems: 'center', p: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                  <Grid item xs={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{idx + 1}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField 
                      placeholder="Nama perusahaan"
                      value={row.companyName} 
                      onChange={(e) => handleArrayChange(idx, 'companyName', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={1.5}>
                    <TextField 
                      placeholder="Contoh: 2019-2021"
                      value={row.period} 
                      onChange={(e) => handleArrayChange(idx, 'period', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField 
                      placeholder="Jabatan"
                      value={row.position} 
                      onChange={(e) => handleArrayChange(idx, 'position', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={1.5}>
                    <TextField 
                      placeholder="Gaji Bersih"
                      value={row.basicSalary} 
                      onChange={(e) => handleArrayChange(idx, 'basicSalary', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      InputProps={{
                        startAdornment: 'Rp ',
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField 
                      placeholder="Tunjangan"
                      value={row.allowance} 
                      onChange={(e) => handleArrayChange(idx, 'allowance', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      // InputProps={{
                      //   startAdornment: 'Rp ',
                      // }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField 
                      placeholder="Alasan pindah"
                      value={row.movingReason} 
                      onChange={(e) => handleArrayChange(idx, 'movingReason', e.target.value)} 
                      fullWidth 
                      size="small"
                      multiline
                      rows={3}
                      variant="outlined"
                      sx={{
                        minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      placeholder="Uraian pekerjaan / tugas pokok"
                      value={row.jobDescription || ''}
                      onChange={(e) => handleArrayChange(idx, 'jobDescription', e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      variant="outlined"
                      sx={{
                        minWidth: 350,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      size="small" 
                      onClick={() => handleArrayRemove(idx)}
                      variant="outlined"
                      color="error"
                      disabled={internalWorkHistory.length === 1}
                      sx={{ minWidth: 60 }}
                    >
                      Hapus
                    </Button>
                  </Grid>
                </Grid>
              ))}
              
              <Button 
                type="button" 
                size="small" 
                onClick={addWorkHistory}
                variant="contained"
                sx={{ 
                  mt: 2,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  }
                }}
              >
                + Tambah Pekerjaan
              </Button>
            </Paper>
          </Grid>



          {/* References Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#495057' }}>
                Berikan minimal 2 kontak rekan kerja dari perusahaan sebelumnya
                <br/>
                <span style={{ fontSize: '0.9em', color: '#666', fontWeight: 'normal' }}>(Give 2 minimum contacts who can give us your employment references)</span>
              </Typography>
              <Grid container spacing={3}>
                {/* Reference 1 */}
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
                    Referensi 1
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField 
                        label="Nama Lengkap" 
                        value={form.reference1Name || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, reference1Name: e.target.value }))}
                        fullWidth 
                        size="medium"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        label="Jabatan/Perusahaan" 
                        value={form.reference1Position || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, reference1Position: e.target.value }))}
                        fullWidth 
                        size="medium"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        label="No. Telepon" 
                        value={form.reference1Phone || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, reference1Phone: e.target.value }))}
                        fullWidth 
                        size="medium"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        label="Email" 
                        value={form.reference1Email || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, reference1Email: e.target.value }))}
                        fullWidth 
                        size="medium"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Reference 2 */}
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
                    Referensi 2
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField 
                        label="Nama Lengkap" 
                        value={form.reference2Name || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, reference2Name: e.target.value }))}
                        fullWidth 
                        size="medium"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        label="Jabatan/Perusahaan" 
                        value={form.reference2Position || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, reference2Position: e.target.value }))}
                        fullWidth 
                        size="medium"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        label="No. Telepon" 
                        value={form.reference2Phone || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, reference2Phone: e.target.value }))}
                        fullWidth 
                        size="medium"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        label="Email" 
                        value={form.reference2Email || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, reference2Email: e.target.value }))}
                        fullWidth 
                        size="medium"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Awards Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#495057' }}>
                Penghargaan yang pernah diterima
                <br/>
                <span style={{ fontSize: '0.9em', color: '#666', fontWeight: 'normal' }}>(Awards you have received)</span>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                    1.
                  </Typography>
                  <TextField 
                    placeholder="Nama penghargaan yang pernah Anda terima..."
                    value={form.award1 || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, award1: e.target.value }))}
                    fullWidth 
                    size="medium"
                    variant="outlined"
                    multiline
                    rows={2}
                    sx={{
                      minWidth: 500,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                    2.
                  </Typography>
                  <TextField 
                    placeholder="Nama penghargaan yang pernah Anda terima..."
                    value={form.award2 || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, award2: e.target.value }))}
                    fullWidth 
                    size="medium"
                    variant="outlined"
                    multiline
                    rows={2}
                    sx={{
                      minWidth: 500,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                    3.
                  </Typography>
                  <TextField 
                    placeholder="Nama penghargaan yang pernah Anda terima..."
                    value={form.award3 || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, award3: e.target.value }))}
                    fullWidth 
                    size="medium"
                    variant="outlined"
                    multiline
                    rows={2}
                    sx={{
                      minWidth: 500,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

        </Grid>
      </Paper>
    </Box>
  );
};

export default Step3PengalamanKerja;
